import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Truck, Search } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import AdminLayout from './AdminLayout';
import ShippingLabelModal from '../../components/ShippingLabelModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminOrders = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(0);
  const [statusFilter, setStatusFilter] = useState('');
  // 'placed' (default): real orders — paid, or progressed by staff (POS).
  // 'abandoned': unpaid checkouts never submitted for real. 'all': everything.
  const [view, setView] = useState('placed');
  const [shippingOrder, setShippingOrder] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => { setQuery(search.trim()); setPage(0); }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchOrders = useCallback(async () => {
    const request = ++requestId.current;
    setLoading(true);
    setError('');
    try {
      const params = { view, search: query || undefined, skip: page * 50, limit: 51 };
      if (statusFilter) params.status = statusFilter;
      const response = await axios.get(`${API_URL}/api/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      if (request !== requestId.current) return;
      setHasMore(response.data.length > 50);
      setOrders(response.data.slice(0, 50));
    } catch (error) {
      if (request === requestId.current) { setOrders([]); setHasMore(false); setError('Orders could not be loaded. Please try again.'); }
    } finally {
      if (request === requestId.current) setLoading(false);
    }
  }, [token, statusFilter, view, query, page]);
  useEffect(() => { fetchOrders(); return () => { requestId.current += 1; }; }, [fetchOrders]);

  const statusColors = {
    pending: 'bg-yellow-500/10 text-yellow-500',
    confirmed: 'bg-blue-500/10 text-blue-500',
    in_production: 'bg-purple-500/10 text-purple-500',
    ready_for_pickup: 'bg-cyan-500/10 text-cyan-500',
    shipped: 'bg-indigo-500/10 text-indigo-500',
    delivered: 'bg-green-500/10 text-green-500',
    completed: 'bg-green-500/10 text-green-500',
    cancelled: 'bg-red-500/10 text-red-500'
  };

  return (
    <AdminLayout>
      <div data-testid="admin-orders">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold">Orders</h1>
        </div>

        {/* View tabs */}
        <div className="flex flex-wrap items-center gap-1 mb-4 border-b border-border" data-testid="orders-view-tabs">
          {[
            { key: 'placed', label: 'Orders' },
            { key: 'abandoned', label: 'Abandoned Checkouts' },
            { key: 'all', label: 'All' },
          ].map((t) => (
            <button key={t.key} onClick={() => { setView(t.key); setPage(0); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                view === t.key
                  ? 'border-[#F5A623] text-[#1B3A5C]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`orders-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <label className="relative w-full sm:max-w-md">
            <span className="sr-only">Search orders</span>
            <Search className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
            <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Name, email, phone, or order number"
              className="w-full min-h-[44px] pl-10 pr-3 py-2 border border-border rounded bg-input text-sm" data-testid="order-search" />
          </label>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            className="px-4 py-2 bg-input border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary"
            aria-label="Order status" data-testid="status-filter"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="in_production">In Production</option>
            <option value="ready_for_pickup">Ready for Pickup</option>
            <option value="shipped">Shipped</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {error && <p role="alert" className="mb-4 text-red-700">{error} <button onClick={fetchOrders} className="underline">Try again</button></p>}
        {/* Orders Table */}
        <div className="bg-card border border-border rounded-lg overflow-x-auto">
          {loading ? (
            <div className="p-6 text-center text-muted-foreground">Loading...</div>
          ) : orders.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground">
              {query ? 'No matching orders in this tab.' : view === 'abandoned' ? 'No abandoned checkouts — nice.' : 'No orders found'}
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left p-4 text-sm font-medium">Order / contact</th>
                  <th className="text-left p-4 text-sm font-medium">Date</th>
                  <th className="text-left p-4 text-sm font-medium">Status</th>
                  <th className="text-left p-4 text-sm font-medium">Items</th>
                  <th className="text-right p-4 text-sm font-medium">Total</th>
                  <th className="text-right p-4 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} className="border-b border-border hover:bg-accent/50">
                    <td className="p-4">
                      <Link 
                        to={`/admin/orders/${order.id}`}
                        className="block min-w-[10rem] max-w-sm hover:text-primary"
                      >
                        <span className="block font-medium break-words">{order.customer_name || order.customer_email || 'Contact name unavailable'}</span>
                        <span className="block mt-1 text-sm text-muted-foreground break-words">{order.in_store_ticket || order.order_number}</span>
                      </Link>
                    </td>
                    <td className="p-4 text-sm text-muted-foreground">
                      {new Date(order.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors[order.status] || 'bg-muted'}`}>
                        {order.status.replace('_', ' ')}
                      </span>
                      <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                        order.payment_status === 'Paid in Full' ? 'bg-green-500/10 text-green-600'
                        : order.payment_status === 'Deposit Paid' ? 'bg-blue-500/10 text-blue-500'
                        : 'bg-red-500/10 text-red-500'
                      }`} data-testid={`payment-pill-${order.id}`}>
                        {order.payment_status || 'Unpaid'}
                      </span>
                      {order.auto_label_status === 'failed' && (
                        <span className="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-red-500/10 text-red-500"
                          title={order.auto_label_error || 'Auto-label failed'}
                          data-testid={`label-failed-pill-${order.id}`}>
                          Label Failed
                        </span>
                      )}
                      {order.auto_label_status === 'success' && (
                        <span className="ml-2 px-2 py-1 text-xs font-medium rounded-full bg-green-500/10 text-green-600"
                          title="Auto-label generated"
                          data-testid={`label-success-pill-${order.id}`}>
                          Label OK
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-muted-foreground">
                      {order.items.length} items
                    </td>
                    <td className="p-4 text-right font-medium">
                      ${order.total.toFixed(2)}
                    </td>
                    <td className="p-4 text-right">
                      <button
                        onClick={() => setShippingOrder(order)}
                        className="p-2 inline-flex hover:bg-accent rounded transition-colors text-[#1B3A5C]"
                        title="Create shipping label"
                        data-testid={`ship-order-${order.id}`}
                      >
                        <Truck className="w-4 h-4" />
                      </button>
                      <Link
                        to={`/admin/orders/${order.id}`}
                        className="p-2 inline-flex hover:bg-accent rounded transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 mt-4 text-sm">
          <button disabled={loading || page === 0} onClick={() => setPage(p => p - 1)} className="min-h-[44px] px-3 border rounded disabled:opacity-40">Previous</button>
          <span>Page {page + 1}</span>
          <button disabled={loading || !hasMore} onClick={() => setPage(p => p + 1)} className="min-h-[44px] px-3 border rounded disabled:opacity-40">Next</button>
        </div>
      </div>
      {shippingOrder && (
        <ShippingLabelModal
          order={shippingOrder}
          onClose={() => setShippingOrder(null)}
          onLabelCreated={() => fetchOrders()}
        />
      )}
    </AdminLayout>
  );
};

export default AdminOrders;
